import React from 'react';
import { useTranslation } from 'react-i18next';
import { Namespace } from '../../../../../translation/namespace';
import { Modifier } from '../../../../../lib/bemUtils/Modifier';
import Bem from '../../../../../lib/bemUtils/bem';

type InputCounterProps = {
  maxLength: number;
  currentCount: number | 0;
  modifier?: Modifier<'visible'>;
};

export default function InputCounter({ currentCount, maxLength, modifier }: InputCounterProps) {
  const { t } = useTranslation(Namespace.InputCounter);

  /**
   * 10% of maxLength characters before maxLength we change the counter look to orange (warning).
   * When we reach maxlength, it's set to red (error).
   * We additionally add an invisible border for people using WIN HCM
   * @return {string}
   */
  const changeCounterLook = () => {
    const percentage = Math.round((10 / 100) * maxLength);

    if (currentCount >= maxLength - percentage && currentCount <= maxLength) {
      return 'input-counter__count input-counter__count--warning';
    }
    if (currentCount > maxLength) {
      return 'input-counter__count input-counter__count--error';
    }
    return 'input-counter__count';
  };

  /**
   * Calculates the remaining chars for screen reader output.
   * Generates the message sent to screen readers.
   * @return {function}
   */
  const getScreenReaderOutput = () => {
    const remainingChars = maxLength - currentCount;
    if (maxLength >= currentCount) return t('screenReaderMessageWarning', { remainingChars });
    if (maxLength < currentCount) return t('screenReaderMessageError', { currentCount });
    return '';
  };

  /**
   * Counter consists of two elements: the counter and a corresponding live region.
   * Live region is display:none until corresponding input field is focused (see CSS).
   * Counter is aria-hidden="true" to prevent reading via screen reader focus.
   * @return {*}
   */
  return (
    <span className={Bem('input-counter', modifier)}>
      <span className={changeCounterLook()} aria-hidden="true">
        {`${currentCount} / ${maxLength}`}
      </span>
      <span aria-live="polite" aria-atomic="true" className="input-counter__live-region">
        {getScreenReaderOutput()}
      </span>
    </span>
  );
}
